import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { useOverlayContext } from "../../functional-components/OverlayContext";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import classNames from "classnames";
import { useMediaQuery } from "react-responsive";
import useIsMounted from "../../hooks/useIsMounted";
import { RoundIconButton } from "../RoundIconButton";
import { RemoveScroll } from "react-remove-scroll";

export default function SimpleOverlay({
  close,
  closeOnClickOutside = true,
  className = "",
  animateScale = false,
  scrollable = false,
  children
}: {
  close?: () => void;
  closeOnClickOutside?: boolean;
  className?: string;
  animateScale?: boolean;
  scrollable?: boolean;
  children: ReactNode;
}) {
  const isMounted = useIsMounted();
  const modalRef = useRef<HTMLDivElement>(null);
  const [modalRoot, setModalRoot] = useState<HTMLElement>();
  const portalId = "simple-overlay-portal-root";
  const { setOverlayOpened } = useOverlayContext();
  const [opened, setOpened] = useState(false);
  const isWide = useMediaQuery({ minWidth: 768 });

  useLayoutEffect(() => {
    let _modalRoot = document.getElementById(portalId);
    if (!_modalRoot) {
      _modalRoot = document.createElement("div");
      _modalRoot.setAttribute("id", portalId);
      document.body.append(_modalRoot);
    }
    setModalRoot(_modalRoot);
  }, []);

  useLayoutEffect(() => {
    setTimeout(() => {
      if (isMounted() && modalRef.current) {
        setOpened(true);
        setOverlayOpened(true);
      }
    }, 100);
    return () => {
      setOverlayOpened(false);
    };
  }, [isMounted, setOverlayOpened]);

  return modalRoot
    ? createPortal(
        <>
          <motion.div
            role="dialog"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            transition={{ duration: 0.2, delay: 0.15 }}
            style={{
              pointerEvents: "auto"
            }}
            className="simple-overlay"
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
              if (closeOnClickOutside && opened) {
                close?.();
              }
            }}
          />
          <motion.div
            ref={modalRef}
            initial={{ opacity: 0, scale: animateScale ? 0.8 : 1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{
              opacity: 0,
              scale: animateScale ? 0.7 : 1,
              transition: { duration: 0.15 }
            }}
            transition={{ type: "spring", damping: 40, stiffness: 400 }}
            className="simple-overlay-content-container"
          >
            <RemoveScroll removeScrollBar={false} enabled forwardProps>
              <div
                className={classNames("simple-overlay-content", className, {
                  "simple-overlay-content-scrollable": scrollable
                })}
              >
                {children}
              </div>
            </RemoveScroll>
            <RoundIconButton
              className={"simple-overlay-close-btn"}
              large
              onClick={() => {
                if (opened) {
                  close?.();
                }
              }}
              style={{
                top: isWide ? "1.4rem" : "0.4rem",
                right: isWide ? "1.4rem" : "0.4rem",
                zIndex: 102
              }}
              icon={"xmark"}
            />
          </motion.div>
          <KeyboardEventHandler
            handleKeys={["esc"]}
            handleFocusableElements
            onKeyEvent={(key, e) => {
              switch (key) {
                case "esc":
                  close?.();
                  e.preventDefault();
                  break;
                default:
              }
            }}
          />
        </>,
        modalRoot
      )
    : null;
}
